'use client';

import { useMemo, useState } from 'react';
import { DatePicker } from './components/date-picker';
import { GlobalFilter } from './components/global-filter';
import { Pagination } from './components/pagination';
import { SortableHeader } from './components/sortable-header';
import { useLlmDataContext } from './context/llm-data-context';
import type { LlmTraceData, TraceRow } from './hooks/mock-llm-data';
import { fuzzySearch, paginateData } from './utils/search';
import { sortData, useSorting } from './utils/sorting';

export const LLMTraces = () => {
  const { traces, loading } = useLlmDataContext();
  const [selectedTrace, setSelectedTrace] = useState<TraceRow | null>(null);
  const [expandedTraces, setExpandedTraces] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [globalFilter, setGlobalFilter] = useState('');
  const [snapshotDate, setSnapshotDate] = useState('2024-01-15');

  const { sort, handleSort } = useSorting({
    key: 'createdAt',
    direction: 'desc',
  });

  // Search fields for fuzzy search
  const searchFields = useMemo(
    () => [
      'modelName',
      'status',
      'functionId',
      'metadata.brandCode',
      'metadata.operationType',
      'metadata.userId',
      'metadata.spanId',
      'metadata.sessionId',
    ],
    [],
  );

  // Filter and paginate data
  const { filteredData, paginatedData, totalPages } = useMemo(() => {
    // Return empty data if still loading or no traces
    if (loading || !traces.length) {
      return {
        filteredData: [],
        paginatedData: [],
        totalPages: 0,
      };
    }

    // Filter by snapshot date
    const dateFiltered = traces.filter((trace) => {
      const traceDate = new Date(trace.createdAt).toISOString().split('T')[0];
      return traceDate === snapshotDate;
    });

    // Apply global filter
    const filtered = fuzzySearch(dateFiltered, globalFilter, searchFields);

    // Group traces hierarchically
    const parentTraces = filtered.filter((trace) => trace.parentSpanId === null);
    const childTraces = filtered.filter((trace) => trace.parentSpanId !== null);

    const grouped = parentTraces.map((parent) => ({
      ...parent,
      children: childTraces.filter((child) => child.parentSpanId === parent.id),
    }));

    // Apply sorting to parent traces only
    let sortedGrouped = grouped;
    if (sort) {
      sortedGrouped = sortData(grouped, sort.key, sort.direction);
    }

    // Flatten for pagination (we'll handle hierarchy in rendering)
    const flattened: TraceRow[] = [];
    sortedGrouped.forEach((parent) => {
      flattened.push(parent);
      if (expandedTraces.has(parent.id)) {
        flattened.push(
          ...parent.children.map((child: LlmTraceData) => ({
            ...child,
            isChild: true,
          })),
        );
      }
    });

    const totalPages = Math.ceil(flattened.length / pageSize);
    const paginated = paginateData(flattened, currentPage, pageSize);

    return {
      filteredData: flattened,
      paginatedData: paginated,
      totalPages,
    };
  }, [
    loading,
    traces,
    globalFilter,
    snapshotDate,
    expandedTraces,
    currentPage,
    pageSize,
    sort,
    searchFields,
  ]);

  const toggleExpanded = (traceId: string) => {
    const newExpanded = new Set(expandedTraces);
    if (newExpanded.has(traceId)) {
      newExpanded.delete(traceId);
    } else {
      newExpanded.add(traceId);
    }
    setExpandedTraces(newExpanded);
    setCurrentPage(1); // Reset to first page when expanding/collapsing
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const TraceRowComponent = ({
    traceRow,
    isChild = false,
  }: {
    traceRow: TraceRow;
    isChild?: boolean;
  }) => (
    <tr
      key={traceRow.id}
      className={`hover:bg-gray-50 cursor-pointer ${
        isChild || traceRow.isChild ? 'bg-gray-25' : ''
      }`}
      onClick={() => setSelectedTrace(traceRow)}
    >
      <td className={`px-4 py-2 text-sm ${isChild || traceRow.isChild ? 'pl-8' : ''}`}>
        {!isChild && !traceRow.isChild && traceRow.children && traceRow.children.length > 0 && (
          <button
            type='button'
            onClick={(e) => {
              e.stopPropagation();
              toggleExpanded(traceRow.id);
            }}
            className='mr-2 text-gray-500 hover:text-gray-700'
          >
            {expandedTraces.has(traceRow.id) ? '▼' : '▶'}
          </button>
        )}
        {(isChild || traceRow.isChild) && <span className='mr-2 text-gray-400'>└─</span>}
        {formatDate(traceRow.createdAt.toString())}
      </td>
      <td className='px-4 py-2 text-sm'>{traceRow.modelName}</td>
      <td className='px-4 py-2'>
        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(traceRow.status)}`}>
          {traceRow.status}
        </span>
      </td>
      <td className='px-4 py-2 text-sm text-right'>{traceRow.inputTokens}</td>
      <td className='px-4 py-2 text-sm text-right'>{traceRow.outputTokens}</td>
      <td className='px-4 py-2 text-sm text-right'>
        ${traceRow.totalCost ? parseFloat(traceRow.totalCost).toFixed(4) : '0.0000'}
      </td>
      <td className='px-4 py-2 text-sm text-right'>{traceRow.latencyMs}</td>
      <td className='px-4 py-2 text-sm'>{traceRow.functionId}</td>
      <td className='px-4 py-2 text-sm'>{(traceRow.metadata as any)?.brandCode}</td>
      <td className='px-4 py-2 text-sm'>{(traceRow.metadata as any)?.operationType}</td>
      <td className='px-4 py-2 text-sm'>{(traceRow.metadata as any)?.userId}</td>
      <td className='px-4 py-2 font-mono text-xs'>{(traceRow.metadata as any)?.spanId}</td>
      <td className='px-4 py-2 font-mono text-xs'>{(traceRow.metadata as any)?.sessionId}</td>
    </tr>
  );

  return (
    <div className='space-y-4'>
      <div className='flex justify-between items-center'>
        <h2 className='text-xl font-semibold'>LLM Traces</h2>
        <div className='text-sm text-gray-500'>{filteredData.length} traces</div>
      </div>

      {/* Toolbar */}
      <div className='bg-white p-4 rounded-lg shadow space-y-4'>
        <div className='flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between'>
          <DatePicker value={snapshotDate} onChange={setSnapshotDate} />
          <div className='w-full sm:w-80'>
            <GlobalFilter
              value={globalFilter}
              onChange={setGlobalFilter}
              placeholder='Search traces...'
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className='bg-white rounded-lg shadow overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50 sticky top-0 z-10'>
              <tr>
                <SortableHeader sortKey='createdAt' currentSort={sort} onSort={handleSort}>
                  Created
                </SortableHeader>
                <SortableHeader sortKey='modelName' currentSort={sort} onSort={handleSort}>
                  Model
                </SortableHeader>
                <SortableHeader sortKey='status' currentSort={sort} onSort={handleSort}>
                  Status
                </SortableHeader>
                <SortableHeader
                  sortKey='inputTokens'
                  currentSort={sort}
                  onSort={handleSort}
                  align='right'
                >
                  Input Tokens
                </SortableHeader>
                <SortableHeader
                  sortKey='outputTokens'
                  currentSort={sort}
                  onSort={handleSort}
                  align='right'
                >
                  Output Tokens
                </SortableHeader>
                <SortableHeader
                  sortKey='totalCost'
                  currentSort={sort}
                  onSort={handleSort}
                  align='right'
                >
                  Total Cost
                </SortableHeader>
                <SortableHeader
                  sortKey='latencyMs'
                  currentSort={sort}
                  onSort={handleSort}
                  align='right'
                >
                  Latency (ms)
                </SortableHeader>
                <SortableHeader sortKey='functionId' currentSort={sort} onSort={handleSort}>
                  Function
                </SortableHeader>
                <SortableHeader sortKey='metadata.brandCode' currentSort={sort} onSort={handleSort}>
                  Brand Code
                </SortableHeader>
                <SortableHeader
                  sortKey='metadata.operationType'
                  currentSort={sort}
                  onSort={handleSort}
                >
                  Operation
                </SortableHeader>
                <SortableHeader sortKey='metadata.userId' currentSort={sort} onSort={handleSort}>
                  User ID
                </SortableHeader>
                <SortableHeader sortKey='metadata.spanId' currentSort={sort} onSort={handleSort}>
                  Span ID
                </SortableHeader>
                <SortableHeader sortKey='metadata.sessionId' currentSort={sort} onSort={handleSort}>
                  Session ID
                </SortableHeader>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {paginatedData.map((traceRow) => (
                <TraceRowComponent key={traceRow.id} traceRow={traceRow} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={filteredData.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Detail Panel */}
      {selectedTrace && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto w-full mx-4'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-semibold'>Trace Details</h3>
              <button
                type='button'
                onClick={() => setSelectedTrace(null)}
                className='text-gray-500 hover:text-gray-700'
              >
                ✕
              </button>
            </div>

            <div className='space-y-4'>
              {selectedTrace.systemPrompt && (
                <div>
                  <h4 className='font-medium text-gray-700 mb-2'>System Prompt</h4>
                  <div className='bg-gray-100 p-3 rounded text-sm'>
                    {selectedTrace.systemPrompt}
                  </div>
                </div>
              )}

              <div>
                <h4 className='font-medium text-gray-700 mb-2'>Input Messages</h4>
                <pre className='bg-gray-100 p-3 rounded text-sm overflow-x-auto'>
                  {JSON.stringify(selectedTrace.inputMessages, null, 2)}
                </pre>
              </div>

              <div>
                <h4 className='font-medium text-gray-700 mb-2'>Output Content</h4>
                <pre className='bg-gray-100 p-3 rounded text-sm overflow-x-auto'>
                  {JSON.stringify(selectedTrace.outputContent, null, 2)}
                </pre>
              </div>

              <div>
                <h4 className='font-medium text-gray-700 mb-2'>Metadata</h4>
                <pre className='bg-gray-100 p-3 rounded text-sm overflow-x-auto'>
                  {JSON.stringify(selectedTrace.metadata, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
